import { useAuthQuery } from '@/hooks/use-auth-query';
import { clearSessionQueries } from '@/lib/query-client';
import { AuthService } from '@/services/auth.service';
import type { User } from '@/types';
import type { ChangePasswordDto, RegisterUserDto, SignInUserDto } from '@/types/dto/auth.dto';
import type { QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import 'react';
import { AuthContext } from './AuthContext';

async function logoutFromSession() {
  try {
    await AuthService.logout();
  } catch {
    // Logging out locally must remain possible after the server session expires.
  }
  clearSessionQueries();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useAuthQuery();

  const user = data ?? null;

  const refreshAuthState = async ({ includeCompany }: { includeCompany: boolean }) => {
    const keys: QueryKey[] = [
      ['auth', 'profile'],
      ['auth', 'companies'],
    ];
    if (includeCompany) {
      keys.push(['companies', 'my']);
    }

    await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    await Promise.all(keys.map((queryKey) => queryClient.refetchQueries({ queryKey, type: 'active' })));
  };

  const signIn = async (data: SignInUserDto) => {
    await AuthService.signIn(data);
    await refreshAuthState({ includeCompany: false });
  };

  const signUp = async (data: RegisterUserDto) => {
    await AuthService.register(data);
    await refreshAuthState({ includeCompany: false });
  };

  const changePassword = async (data: ChangePasswordDto) => {
    await AuthService.changePassword(data);
    queryClient.setQueryData<User | null>(['auth', 'profile'], (current) => {
      if (!current) return current;
      return { ...current, mustChangePassword: false };
    });
    await refreshAuthState({ includeCompany: true });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    signIn,
    signUp,
    changePassword,
    logout: logoutFromSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
